//Ashwin
import React, { useState, useEffect } from "react";
import axios from "axios";
import Select, { components } from "react-select";

import {
  FaCalendarCheck,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Badge,
  Modal,
} from "react-bootstrap";
import { getActionPermissions } from "../../Utils/ActionPermissionChecker";
import { showMyAlert } from "../../Utils/alertService";
import { doAuditLog } from "../../Utils/saveHistoryAction";

const CustomSingleValue = (props) => (
  <components.SingleValue {...props}>{props.data.value}</components.SingleValue>
);

const LeaveRecord = () => {
  const userRole = sessionStorage.getItem("role");
  const { canView, canAdd, canEdit, canDelete } =
    getActionPermissions(userRole);
  const [users, setUsers] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState("");
  const [empName, setEmpName] = useState("");
  const [leaveDays, setLeaveDays] = useState(1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [editingLeave, setEditingLeave] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [shift, setShift] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState("");
  const [confirmModalItem, setConfirmModalItem] = useState({});

  const companyKey = sessionStorage.getItem("companyKey");

  const isMobile = window.innerWidth < 768;

  const userApi =
    "https://api.dremerz.net/api/DZ_8922_Payroll_PayrollRegistration_PayrollRegistrationUsers";
  const leaveApi =
    "https://api.dremerz.net/api/DZ_8921_Payroll_PayrollRegistration_PayrollRegistrationUsers_LeaveRecords";

  useEffect(() => {
    fetchUsers();
    fetchLeaveData();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(userApi);
      const filteredUsers = response.data.filter((u)=> u.companyKey === companyKey);
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to fetch users. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const fetchLeaveData = async () => {
    try {
      const response = await axios.get(leaveApi);
      const filteredLeaves = response.data.filter((l)=> l.companyKey === companyKey);
      setLeaveData(filteredLeaves);
    } catch (error) {
      console.error("Error fetching leave data:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to fetch leave records. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const handleEmpIdChange = (selectedOption) => {
    if (selectedOption) {
      const empId = selectedOption.value;
      setSelectedEmp(empId);
      const selectedUser = users.find((user) => user.employee_id === empId);
      if (selectedUser) {
        setEmpName(selectedUser.full_name);
        setLocationName(selectedUser.locationName || "");
        try {
          const parsedShift = JSON.parse(selectedUser.shift || "[]");
          const formattedShift = parsedShift
            .map(
              (s) =>
                `${s.shiftName} (${s.startTime} - ${s.endTime}, ${s.workingHours} hrs)`
            )
            .join(", ");
          setShift(formattedShift);
        } catch (e) {
          setShift("Invalid shift data");
        }
      } else {
        setEmpName("");
        setLocationName("");
        setShift("");
      }
    } else {
      setSelectedEmp("");
      setEmpName("");
      setLocationName("");
      setShift("");
    }
  };

  const handleLeaveDaysChange = (e) => {
    const days = parseInt(e.target.value, 10);
    setLeaveDays(days);
    if (days === 1) {
      setToDate("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const leaveDetails = {
      employee_id: selectedEmp,
      employee_name: empName,
      leave_days: leaveDays,
      from_date: fromDate,
      to_date: leaveDays > 1 ? toDate : fromDate,
      reason: reason,
      status: "approved",
    };

    try {
      if (editingLeave) {
        await axios.put(
          `https://api.dremerz.net/api/DZ_8921_Payroll_PayrollRegistration_PayrollRegistrationUsers_LeaveRecords/${editingLeave.id}`,
          leaveDetails
        );
        doAuditLog("Update", "Leave Management", editingLeave, leaveDetails);
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Updated",
          message: "Leave records updated successfully",
          duration: 2500, // auto-close in 2.5s
        });
        setEditingLeave(null);
      } else {
        await axios.post(
          "https://api.dremerz.net/api/DZ_8921_Payroll_PayrollRegistration_PayrollRegistrationUsers_LeaveRecords",
          {
            ...leaveDetails,
            companyKey: companyKey,
          }
        );
        doAuditLog("Add", "Leave Management", "", leaveDetails);
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Submitted",
          message: "Leave records submitted successfully",
          duration: 2500, // auto-close in 2.5s
        });
      }
      fetchLeaveData();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error("Error submitting leave:", error);
      showMyAlert({
        type: "danger", // "success" | "danger" | "warning" | "info"
        title: "Failed",
        message: "Failed to submit leave records. Please try again.",
        duration: 2500, // auto-close in 2.5s
      });
    }
  };

  const handleEdit = (leave) => {
    setEditingLeave(leave);
    setSelectedEmp(leave.employee_id);
    setEmpName(leave.employee_name);
    setLeaveDays(leave.leave_days);
    setFromDate(leave.from_date.split("T")[0]);
    setToDate(leave.to_date ? leave.to_date.split("T")[0] : "");
    setReason(leave.reason);

    const selectedUser = users.find(
      (user) => user.employee_id === leave.employee_id
    );
    if (selectedUser) {
      setLocationName(selectedUser.locationName || "");
      try {
        const parsedShift = JSON.parse(selectedUser.shift || "[]");
        const formattedShift = parsedShift
          .map(
            (s) =>
              `${s.shiftName} (${s.startTime} - ${s.endTime}, ${s.workingHours} hrs)`
          )
          .join(", ");
        setShift(formattedShift);
      } catch (e) {
        setShift("Invalid shift data");
      }
    }
    setShowModal(true);
  };

  const handleDelete = async (id, leave) => {
    setConfirmModalMessage("Are you sure you want to delete this leave record?");
    setShowConfirmModal(true);
    setConfirmModalItem({id, leave});
  };

  const handleDeleteConfirm = async() =>{
      try {
        await axios.delete(
          `https://api.dremerz.net/api/DZ_8921_Payroll_PayrollRegistration_PayrollRegistrationUsers_LeaveRecords/${confirmModalItem.id}`
        );
        doAuditLog("Delete", "Leave Management", confirmModalItem.leave, "");
        showMyAlert({
          type: "success", // "success" | "danger" | "warning" | "info"
          title: "Deleted",
          message: "Leave records Deleted successfully",
          duration: 2500, // auto-close in 2.5s
        });
        fetchLeaveData();
      } catch (error) {
        console.error("Error deleting leave:", error);
        showMyAlert({
          type: "danger", // "success" | "danger" | "warning" | "info"
          title: "Failed",
          message: "Failed to delete leave record. Please try again",
          duration: 2500, // auto-close in 2.5s
        });
      } finally {
        setConfirmModalMessage("");
        setShowConfirmModal(false);
        setConfirmModalItem({});
      }
  }
  
  const resetForm = () => {
    setSelectedEmp("");
    setEmpName("");
    setLeaveDays(1);
    setFromDate("");
    setToDate("");
    setReason("");
    setEditingLeave(null);
    setLocationName("");
    setShift("");
  };

  const handleCloseModal = () => {
    resetForm();
    setShowModal(false);
  };

  const handleDownload = () => {
    const filteredData = getFilteredData().map((leave) => ({
      "Employee ID": leave.employee_id,
      "Employee Name": leave.employee_name,
      "Leave Days": leave.leave_days,
      "From Date": new Date(leave.from_date).toLocaleDateString(),
      "To Date": leave.to_date
        ? new Date(leave.to_date).toLocaleDateString()
        : new Date(leave.from_date).toLocaleDateString(),
      Reason: leave.reason,
      Status: leave.status,
    }));

    if (filteredData.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data to download for selected criteria",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Records");
    XLSX.writeFile(workbook, "LeaveRecords.xlsx");
  };

  const handleDownloadPDF = () => {
    const filteredData = getFilteredData().map((leave) => [
      leave.employee_id,
      leave.employee_name,
      leave.leave_days,
      new Date(leave.from_date).toLocaleDateString(),
      leave.to_date
        ? new Date(leave.to_date).toLocaleDateString()
        : new Date(leave.from_date).toLocaleDateString(),
      leave.reason,
      leave.status,
    ]);

    if (filteredData.length === 0) {
      showMyAlert({
        type: "info", // "success" | "danger" | "warning" | "info"
        title: "No Data",
        message: "No data to download for selected criteria",
        duration: 2500, // auto-close in 2.5s
      });
      return;
    }

    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Leave Records", 14, 16);
    doc.autoTable({
      head: [
        [
          "Employee ID",
          "Employee Name",
          "Leave Days",
          "From Date",
          "To Date",
          "Reason",
          "Status",
        ],
      ],
      body: filteredData,
      startY: 22,
      styles: { fontSize: 10, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { left: 14, right: 14 },
    });
    doc.save("Leave_Records.pdf");
  };

  const getFilteredData = () => {
    return leaveData
      .filter((leave) => {
        const fromDateMatch =
          !filterFromDate ||
          new Date(leave.from_date) >= new Date(filterFromDate);
        const toDateMatch =
          !filterToDate || new Date(leave.to_date) <= new Date(filterToDate);
        const searchTermMatch =
          !searchTerm ||
          leave.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          leave.employee_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          leave.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
          new Date(leave.from_date)
            .toLocaleDateString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (leave.to_date &&
            new Date(leave.to_date)
              .toLocaleDateString()
              .toLowerCase()
              .includes(searchTerm.toLowerCase()));
        const statusMatch =
          statusFilter === "all" || leave.status === statusFilter;

        return fromDateMatch && toDateMatch && searchTermMatch && statusMatch;
      })
      .toReversed();
  };

  const userOptions = users.map((user) => ({
    value: user.employee_id,
    label: `${user.employee_id} - ${user.full_name}`,
  }));

  const filteredLeaveData = getFilteredData();

  // Calculate statistics
  const totalLeaves = filteredLeaveData.length;
  const approvedLeaves = filteredLeaveData.filter(
    (leave) => leave.status === "approved"
  ).length;
  const pendingLeaves = filteredLeaveData.filter(
    (leave) => leave.status === "pending"
  ).length;

  return (
    <Container fluid className="mt-3">
      {/* Header Section */}
      <div className="row align-items-center mb-2 border rounded bg-light py-2 mx-1">
        <div className="col-12 col-md-6 mb-2 mb-md-0">
          <h5 className="mb-0 d-flex align-items-center justify-content-center justify-content-md-start gap-2">
            Leave Management
          </h5>
        </div>
        <div className="col-12 col-md-6 d-flex justify-content-center justify-content-md-end gap-2">
          <Button variant="outline-success" size="sm" onClick={handleDownload}>
            Export Excel
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            title="Download PDF"
            onClick={handleDownloadPDF}
          >
            Export PDF
          </Button>
          {canAdd && (
            <Button
              variant="outline-primary"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => setShowModal(true)}
            >
              <FaPlus size={14} />
              Add Leave
            </Button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="row align-items-center mb-3 border rounded bg-light py-3 mx-1">
        {/* Search Input */}
        <div className="col-12 col-md-4 mb-3 mb-md-0">
          <Form.Group className="mb-0">
            <Form.Label className="small text-muted mb-1">Search</Form.Label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-light border-end-0">
                <FaSearch className="text-muted" />
              </span>
              <Form.Control
                type="text"
                placeholder="Search here..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0"
              />
            </div>
          </Form.Group>
        </div>

        {/* Date Range Filters */}
        <div className="col-12 col-md-6 mb-3 mb-md-0">
          <div className="row g-2 align-items-end">
            <div className="col-6">
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1">
                  Start Date
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filterFromDate}
                  onChange={(e) => setFilterFromDate(e.target.value)}
                  placeholder="dd-mm-yyyy"
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-0">
                <Form.Label className="small text-muted mb-1">
                  End Date
                </Form.Label>
                <Form.Control
                  size="sm"
                  type="date"
                  value={filterToDate}
                  onChange={(e) => setFilterToDate(e.target.value)}
                  min={filterFromDate}
                  placeholder="dd-mm-yyyy"
                />
              </Form.Group>
            </div>
          </div>
        </div>

        {/* Clear Filters Button */}
        <div className="col-12 col-md-2 text-md-start mt-2 mt-md-4">
          <Button
            variant="outline-danger"
            size="sm"
            className=" w-md-auto form-control"
            onClick={() => {
              setFilterFromDate("");
              setFilterToDate("");
              setStatusFilter("all");
              setSearchTerm("");
            }}
          >
            Clear
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4 g-4">
        <Col md={6}>
          <Card className="shadow-sm border-top-md border-top-primary">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Total Leaves
                  </h6>
                  <h2 className="mb-0">{totalLeaves}</h2>
                </div>
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <FaCalendarCheck size={24} className="text-primary" />
                </div>
              </div>
              {/* <div className="mt-3">
                <span className="text-success fw-semibold">
                  All leave records
                </span>
              </div> */}
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm border-top-md border-top-success">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Approved
                  </h6>
                  <h2 className="mb-0">{approvedLeaves}</h2>
                </div>
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <FaCheckCircle size={24} className="text-success" />
                </div>
              </div>
              {/* <div className="mt-3">
                <span className="text-success fw-semibold">
                  {approvedLeaves > 0 ? `${((approvedLeaves / totalLeaves) * 100).toFixed(1)}% approved` : "No approved leaves"}
                </span>
              </div> */}
            </Card.Body>
          </Card>
        </Col>
        {/* <Col md={4}>
          <Card className="shadow-sm border-top-md border-top-warning">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-uppercase text-muted fw-semibold mb-2">
                    Pending
                  </h6>
                  <h2 className="mb-0">{pendingLeaves}</h2>
                </div>
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FaClock size={24} className="text-warning" />
                </div>
              </div>
              <div className="mt-3">
                <span className="text-warning fw-semibold">
                  {pendingLeaves > 0 ? `${((pendingLeaves / totalLeaves) * 100).toFixed(1)}% pending` : "No pending leaves"}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>

      {/* Leave Records Table */}
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              Leave Records ({filteredLeaveData.length} records)
            </h6>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {isMobile ? (
            <div>
              {filteredLeaveData.length > 0 ? (
                filteredLeaveData.map((leave, index) => (
                  <div key={leave.id} className="mb-3">
                    <div
                      className="card shadow-sm border-0"
                      style={{ textAlign: "left", borderRadius: "12px" }}
                    >
                      <div className="card-body">
                        <table className="table table-striped table-bordered mb-2">
                          <tbody>
                            <tr>
                              <td className="fw-bold">SI</td>
                              <td>{filteredLeaveData.length - index}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Employee ID</td>
                              <td>{leave.employee_id}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Employee Name</td>
                              <td>{leave.employee_name}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Leave Days</td>
                              <td>{leave.leave_days}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">From Date</td>
                              <td>
                                {new Date(leave.from_date).toLocaleDateString()}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">To Date</td>
                              <td>
                                {leave.to_date
                                  ? new Date(leave.to_date).toLocaleDateString()
                                  : new Date(
                                      leave.from_date
                                    ).toLocaleDateString()}
                              </td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Reason</td>
                              <td>{leave.reason}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Status</td>
                              <td>{leave.status.toUpperCase()}</td>
                            </tr>
                          </tbody>
                        </table>
                        {(canDelete || canEdit) && (
                          <div className="d-flex justify-content-end align-itmes-center gap-2">
                            {canEdit && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={() => handleEdit(leave)}
                              >
                                Edit
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={() => handleDelete(leave.id, leave)}
                              >
                                Delete
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <FaCalendarCheck size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No leave records found</h5>
                  <p className="text-muted">
                    Try adjusting your filters or add a new leave record
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover responsive bordered className="mb-0">
                <thead className="">
                  <tr>
                    <th id="text-white-important">SI</th>
                    <th id="text-white-important">Employee ID</th>
                    <th id="text-white-important">Employee Name</th>
                    <th id="text-white-important" className="">
                      {" "}
                      Days
                    </th>
                    <th id="text-white-important">From Date</th>
                    <th id="text-white-important">To Date</th>
                    <th id="text-white-important">Reason</th>
                    <th id="text-white-important" className="text-center">
                      Status
                    </th>
                    {(canEdit || canDelete) && (
                      <th id="text-white-important" className="text-center">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredLeaveData.length > 0 ? (
                    filteredLeaveData.map((leave, index) => (
                      <tr key={leave.id}>
                        <td>{filteredLeaveData.length - index}</td>
                        <td className="">{leave.employee_id}</td>
                        <td className="">{leave.employee_name}</td>
                        <td className="text-center">
                          {/* <Badge bg="info"> */}
                          {leave.leave_days}
                          {/* </Badge> */}
                        </td>
                        <td>
                          {new Date(leave.from_date).toLocaleDateString()}
                        </td>
                        <td>
                          {leave.to_date
                            ? new Date(leave.to_date).toLocaleDateString()
                            : new Date(leave.from_date).toLocaleDateString()}
                        </td>
                        <td style={{ width: "200px", textWrap: "wrap" }}>
                          {leave.reason}
                        </td>
                        <td className="text-center">
                          {/* <Badge bg={leave.status === 'approved' ? 'success' : 'warning'}> */}
                          {leave.status.toUpperCase()}
                          {/* </Badge> */}
                        </td>
                        {(canDelete || canEdit) && (
                          <td className="text-center">
                            {canEdit && (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => handleEdit(leave)}
                                title="Edit"
                              >
                                {/* <FaEdit /> */}
                                Edit
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(leave.id, leave)}
                                title="Delete"
                              >
                                {/* <FaTrash /> */}
                                Delete
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="py-4">
                          <FaCalendarCheck
                            size={48}
                            className="text-muted mb-3"
                          />
                          <h5 className="text-muted">No leave records found</h5>
                          <p className="text-muted">
                            Try adjusting your filters or add a new leave record
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showConfirmModal}
        onHide={() => {setShowConfirmModal(false); setConfirmModalMessage("")}}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{confirmModalMessage}</p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {setShowConfirmModal(false); setConfirmModalMessage("")}}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit Leave Modal */}
      {showModal && (
        <div
          className="modal fade show d-block my-3"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-centered pt-5">
            <div className="modal-content">
              <div className="modal-header text-black">
                <h5 className="modal-title fw-bold">
                  {editingLeave ? "Edit Leave Record" : "Add New Leave Record"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div
                  className="modal-body"
                  style={{ height: "60vh", overflowY: "auto" }}
                >
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="empId" className="form-label fw-bold">
                        Employee ID
                      </label>
                      <Select
                        id="empId"
                        options={userOptions}
                        value={userOptions.find(
                          (option) => option.value === selectedEmp
                        )}
                        onChange={handleEmpIdChange}
                        isClearable
                        isSearchable
                        required
                        placeholder="Type or select an Employee ID"
                        components={{ SingleValue: CustomSingleValue }}
                        styles={{
                          menu: (base) => ({ ...base, zIndex: 9999 }),
                          menuList: (base) => ({
                            ...base,
                            maxHeight: "150px",
                            overflowY: "auto",
                          }),
                        }}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="empName" className="form-label fw-bold">
                        Employee Name
                      </label>
                      <Form.Control
                        type="text"
                        id="empName"
                        value={empName}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label
                        htmlFor="locationName"
                        className="form-label fw-bold"
                      >
                        Location
                      </label>
                      <Form.Control
                        type="text"
                        id="locationName"
                        value={locationName}
                        readOnly
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="shift" className="form-label fw-bold">
                        Shift
                      </label>
                      <Form.Control
                        type="text"
                        id="shift"
                        value={shift}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label htmlFor="leaveDays" className="form-label fw-bold">
                        Leave Days
                      </label>
                      <Form.Control
                        type="number"
                        id="leaveDays"
                        value={leaveDays}
                        onChange={handleLeaveDaysChange}
                        min="1"
                        required
                      />
                    </div>

                    <div className="col-md-4">
                      <label htmlFor="fromDate" className="form-label fw-bold">
                        {leaveDays > 1 ? "From Date" : "Date"}
                      </label>
                      <Form.Control
                        type="date"
                        id="fromDate"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        required
                      />
                    </div>

                    {leaveDays > 1 && (
                      <div className="col-md-4">
                        <label htmlFor="toDate" className="form-label fw-bold">
                          To Date
                        </label>
                        <Form.Control
                          type="date"
                          id="toDate"
                          value={toDate}
                          onChange={(e) => setToDate(e.target.value)}
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="reason" className="form-label fw-bold">
                      Reason
                    </label>
                    <Form.Control
                      as="textarea"
                      id="reason"
                      rows="3"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      required
                      placeholder="Enter reason for leave..."
                    />
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <div className="d-flex gap-2 justify-content-end">
                    <Button variant="secondary" onClick={handleCloseModal}>
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      {editingLeave ? "Update Leave" : "Submit Leave"}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default LeaveRecord;
